import { Link } from 'react-router-dom';
import { useBlog } from '../../context/BlogContext';
import { usePortfolio } from '../../context/PortfolioContext';
import { useAuth } from '../../context/AuthContext';
import {
  BarChart,
  Edit,
  ExternalLink,
  FileText,
  Folder,
  Layout,
  LogOut,
  PlusCircle,
  Settings,
} from 'lucide-react';

const AdminDashboard = () => {
  const { posts } = useBlog();
  const { projects } = usePortfolio();
  const { logout } = useAuth();

  const stats = [
    {
      title: 'Blog Posts',
      value: posts.length,
      icon: FileText,
      link: '/admin/blog',
    },
    {
      title: 'Portfolio Projects',
      value: projects.length,
      icon: Folder,
      link: '/admin/portfolio',
    },
    {
      title: 'Total Views',
      value: '1,248',
      icon: BarChart,
      link: '#',
    },
    {
      title: 'Settings',
      value: 'Soon',
      icon: Settings,
      link: '#',
    },
  ];

  const recentPosts = posts.slice(0, 5);
  const recentProjects = projects.slice(0, 5);

  return (
    <div className="admin-shell">
      <div className="admin-container">
        <div className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="admin-section-title mb-3">Administration</p>
            <h1 className="text-3xl font-bold sm:text-4xl">Admin Dashboard</h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/" className="btn btn-secondary">
              <ExternalLink className="h-4 w-4" />
              View Website
            </Link>
            <button type="button" onClick={logout} className="btn btn-secondary text-error">
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {stats.map((stat) => {
            const Icon = stat.icon;

            return (
              <Link
                key={stat.title}
                to={stat.link}
                className="admin-panel p-5 transition-colors hover:border-primary/35 hover:bg-card-hover"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm text-foreground/60">{stat.title}</p>
                    <p className="mt-3 text-3xl font-bold">{stat.value}</p>
                  </div>
                  <span className="rounded-lg bg-primary/15 p-3 text-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
          <section className="admin-panel p-5 sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-4">
              <h2 className="text-xl font-bold">Recent Blog Posts</h2>
              <Link to="/admin/blog" className="text-sm text-primary hover:text-primary/80">
                View All
              </Link>
            </div>

            <div className="space-y-3">
              {recentPosts.map((post) => (
                <div key={post.id} className="admin-panel-muted flex items-center justify-between gap-4 p-4">
                  <div className="min-w-0">
                    <h3 className="truncate font-medium">{post.title}</h3>
                    <p className="mt-1 text-sm text-foreground/50">{new Date(post.date).toDateString()}</p>
                  </div>
                  <Link to={`/admin/blog?edit=${post.id}`} className="admin-icon-button" title="Edit post">
                    <Edit className="h-4 w-4" />
                  </Link>
                </div>
              ))}
            </div>

            <Link to="/admin/blog?new=true" className="btn btn-primary mt-5 w-full">
              <PlusCircle className="h-4 w-4" />
              Create New Post
            </Link>
          </section>

          <section className="admin-panel p-5 sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-4">
              <h2 className="text-xl font-bold">Recent Projects</h2>
              <Link to="/admin/portfolio" className="text-sm text-primary hover:text-primary/80">
                View All
              </Link>
            </div>

            <div className="space-y-3">
              {recentProjects.map((project) => (
                <div key={project.id} className="admin-panel-muted flex items-center justify-between gap-4 p-4">
                  <div className="min-w-0">
                    <h3 className="truncate font-medium">{project.title}</h3>
                    <p className="mt-1 text-sm text-foreground/50">{project.category}</p>
                  </div>
                  <Link to={`/admin/portfolio?edit=${project.id}`} className="admin-icon-button" title="Edit project">
                    <Edit className="h-4 w-4" />
                  </Link>
                </div>
              ))}
            </div>

            <Link to="/admin/portfolio?new=true" className="btn btn-primary mt-5 w-full">
              <PlusCircle className="h-4 w-4" />
              Create New Project
            </Link>
          </section>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
          <Link to="/" className="admin-panel p-5 transition-colors hover:border-primary/35 hover:bg-card-hover">
            <Layout className="mb-4 h-7 w-7 text-primary" />
            <h3 className="text-lg font-bold">View Website</h3>
            <p className="mt-2 text-sm text-foreground/60">Preview the public portfolio and blog.</p>
          </Link>
          <Link to="#" className="admin-panel p-5 transition-colors hover:border-primary/35 hover:bg-card-hover">
            <Settings className="mb-4 h-7 w-7 text-primary" />
            <h3 className="text-lg font-bold">Settings</h3>
            <p className="mt-2 text-sm text-foreground/60">Global website settings placeholder.</p>
          </Link>
          <Link to="#" className="admin-panel p-5 transition-colors hover:border-primary/35 hover:bg-card-hover">
            <BarChart className="mb-4 h-7 w-7 text-primary" />
            <h3 className="text-lg font-bold">Analytics</h3>
            <p className="mt-2 text-sm text-foreground/60">Traffic and content performance placeholder.</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
